import type { IDocxTypography, ITextElement } from '@vervedoc/docx-editor-schema'
import { resolveTextFont, type TextMeasure } from './text-measure'

/** 全角标点的空白可压缩；不改变拉丁标点、普通汉字和数字的字形。 */
const PUNCTUATION = /^[、。，．！？：；【】（）《》〈〉「」『』〔〕［］｛｝“”‘’]$/

export interface CompressionMetrics {
  minWidth: number
  leadingRatio: number
}

/** 原文指定等宽东亚排位时，半角空格采用全角空格的一半占宽。 */
export function balancedSpaceWidth(
  run: ITextElement, typography: IDocxTypography | undefined,
  measure: TextMeasure, defaultFont: string, size: number
): number | undefined {
  if (!typography?.balanceSingleByteDoubleByteWidth || run.fontHint !== 'eastAsia' ||
    run.sourceFont !== run.font || !run.fontFamilies?.eastAsia) return undefined
  const font = resolveTextFont(run, '一', defaultFont)
  const narrow = measure.charWidth('i', font, size, run.bold, run.italic)
  const wide = measure.charWidth('W', font, size, run.bold, run.italic)
  const full = measure.charWidth('\u3000', font, size, run.bold, run.italic)
  // 兼容开关只针对等宽字体，不能把比例字体中的空格也强制放大。
  return Math.abs(narrow - wide) < 0.01 && Math.abs(wide * 2 - full) < 0.05 ? full / 2 : undefined
}

/** 测量允许收缩的标点留白，字形本身和至少半个字身宽度始终保留。 */
export function punctuationCompression(
  ch: string, font: string, size: number, bold: boolean, italic: boolean,
  typography: IDocxTypography | undefined, measure: TextMeasure
): CompressionMetrics | undefined {
  const rule = typography?.characterSpacingControl
  if (!rule || rule === 'doNotCompress' || !PUNCTUATION.test(ch)) return undefined
  const width = measure.charWidth(ch, font, size, bold, italic)
  if (width < size * 0.9) return undefined
  const ink = measure.inkBounds(ch, font, size, bold, italic)
  const left = Math.max(0, ink.left)
  const right = Math.max(0, width - ink.right)
  const whitespace = left + right
  const minWidth = Math.max(size / 2, width - whitespace)
  if (minWidth >= width - 0.001) return undefined
  return { minWidth, leadingRatio: left / whitespace }
}
